import { MatDialogRef } from '@angular/material/dialog';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  HostListener,
  OnDestroy,
  ViewChild,
  ElementRef
} from '@angular/core';
import { ChatEntity } from 'src/app/models/entities/chat.entity';
import { UserService } from 'src/app/services/api/user.service';
import { PushNotificationService } from 'src/app/services/pushNotification.service';
import { BoardEntity } from 'src/app/models/entities/board.entity';
import { UserEntity } from 'src/app/models/entities/user.entity';
import { NotificationEntity } from 'src/app/models/entities/notification.entity';
import { ChatService } from 'src/app/services/api/chat.service';
import { ClientService } from 'src/app/services/api/clients.service';
import { NotificationService } from 'src/app/services/api/notifications.service';
import { Timestamp } from '@angular/fire/firestore';
import { Utils } from 'src/app/utils';
import { FileUploadService } from 'src/app/services/file-upload.service';
import { FileUpload } from 'src/app/models/file-upload.model';
import { Observable, Subject } from 'rxjs';
import { CardService } from 'src/app/services/api/card.service';
import { SkeletonLoaderComponent } from '../skeleton-loader/skeleton-loader.component';
import { Subscription } from 'rxjs';
import { finalize, debounceTime } from 'rxjs/operators';
import { BoardService } from 'src/app/services/api/board.service';
import { SharedStateService } from 'src/app/services/shared.state.service';
import { CardEntity } from 'src/app/models/entities/card.entity';
import { AIAgentChatService } from 'src/app/services/api/ai-agent-chat.service';
import { marked } from 'marked';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  standalone: false,
})
export class ChatComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public board: BoardEntity | undefined;
  @Input() public boardKey!: string;
  @Input() public cardKey!: string;
  @Input() public cardName: string | undefined;

  @Input() public users: UserEntity[] = [];
  @Input() public hasFocus: boolean = false;
  @Input() public embedded: boolean = false;
  public usersFiltered: UserEntity[] = [];
  public currentUser: string = '';
  public userKey: string = '';
  public message: string | undefined;
  public selectedImage: string = '';
  public selectedReplyMessage: ChatEntity | null = null;
  public selectedUser!: UserEntity;
  public uploadFolder: string = 'chat';
  public chats: ChatEntity[] = [];
  public chatsFiltered: any[] = [];
  public chatListen: any;
  public mentionList: UserEntity[] = [];
  public disableSend: boolean = false;
  public lastChat: ChatEntity | undefined;
  public codes: { code: string; board: string }[] = [];
  private notifications: { [key: string]: any } = {};
  private client!: string;
  public userNotifications: { [key: string]: number } = {};
  public isLoadingUsers = true;
  public skeletonCount = 5; // Number of skeleton items to show

  private lastLoadedChat: ChatEntity | null = null;
  public isLoadingMore = false;
  private readonly PAGE_SIZE = 10;
  private realtimeSubscription: Subscription | null = null;
  private scrollSubject = new Subject<Event>();
  private scrollSubscription: Subscription | null = null;

  @ViewChild('bubbles') bubblesElement!: ElementRef;

  // AI Agent properties
  private readonly AI_AGENT_USER_KEY = 'flaredeck-agent';
  public aiAgentUser!: UserEntity;
  public currentAISessionId?: string;
  public isAITyping = false;
  private currentUserEntity: UserEntity | null = null;
  public isLoadingHistory = false;

  mentionConfig = {
    mentions: [
      {
        items: [],
        triggerChar: '@',
        labelKey: 'name',
        valueKey: 'name',
      },
      {
        items: [],
        triggerChar: '#',
        labelKey: 'code',
        valueKey: 'code',
      },
    ],
  };

  public hasMoreChats = true; // Add this property to track if there are more messages to load
  private cardLaneCache: Map<string, string> = new Map(); // Cache for card lane info
  private filteredChatsCache: Map<string, ChatEntity[]> = new Map(); // Cache for filtered chats by user

  constructor(
    public dialogRef: MatDialogRef<any>,
    private userService: UserService,
    private push: PushNotificationService,
    private chatService: ChatService,
    private cardService: CardService,
    private clientService: ClientService,
    private uploadService: FileUploadService,
    private notifcationService: NotificationService,
    private boardService: BoardService,
    private sharedStateService: SharedStateService,
    private aiAgentChatService: AIAgentChatService,
    private sanitizer: DomSanitizer
  ) {
    userService.currentUser$.subscribe((user) => {
      if (user) {
        this.currentUser = user.name;
        this.userKey = user.key;
        this.currentUserEntity = user;
      }
    });
    // Initialize hasMoreChats property
    this.hasMoreChats = true;
    // Initialize AI Agent user
    this.initializeAIAgentUser();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['hasFocus']) {
      setTimeout(() => {
        //this.scroll();
        // Only mark as read if we have a selected user and the chat is focused
        if (this.selectedUser && this.hasFocus) {
          this.checkAndMarkAsRead();
        }
      }, 1000);
    }
    if (changes['users']) {

      this.users = changes['users'].currentValue;
      if (this.users) {

        this.users = this.users.filter((x: any) => x);

        this.usersFiltered = this.users;

        // Add AI agent at the top of the mention list
        this.mentionList = [this.aiAgentUser, ...this.users];
        this.updateMentionConfig();

        // Auto-select AI agent by default if no user is selected
        if (!this.selectedUser) {
          setTimeout(() => {
            this.userSelect(this.aiAgentUser, false);
          }, 500);
        }
      }
    }
  }

  @HostListener('window:focus')
  onFocus() {
    
    this.hasFocus = true;
    if (this.selectedUser) {
      this.checkAndMarkAsRead();
    }
  }

  @HostListener('window:blur')
  onBlur() {
    
    this.hasFocus = false;
  }

  @HostListener('scroll', ['$event'])
  handleScroll(event: any): void {
    // Emit to subject for debounced processing
    this.scrollSubject.next(event);
  }

  private handleScrollEvent(event: any): void {
    const element = event.target;
    const scrollPosition = element.scrollTop;
    const scrollThreshold = 50; // Lower threshold to catch scrolling earlier
    const isAtTop = scrollPosition <= scrollThreshold;

    // Show visual indicator when near top
    const loadingIndicator = document.querySelector('.loading-more') as HTMLElement;
    if (loadingIndicator) {
      if (isAtTop && !this.isLoadingMore && this.chatsFiltered.length > 0) {
        loadingIndicator.style.opacity = '0.5';
        loadingIndicator.style.display = 'block';
      } else if (!this.isLoadingMore) {
        loadingIndicator.style.opacity = '0';
        setTimeout(() => {
          if (!this.isLoadingMore) {
            loadingIndicator.style.display = 'none';
          }
        }, 300);
      }
    }

    // Load more when scrolling up and at the top
    if (isAtTop &&
        !this.isLoadingMore &&
        this.chatsFiltered.length > 0 &&
        this.hasMoreChats) {
      this.loadOlderMessages(); // Use the dedicated method for loading more
    }
  }

  trackUser(index: number, user: UserEntity): string {
    return user.key;
  }

  private checkAndMarkAsRead() {
    console.log('checkAndMarkAsRead called', {
      hasSelectedUser: !!this.selectedUser,
      chatCount: this.chatsFiltered?.length,
      hasFocus: this.hasFocus,
    });

    // Skip for AI agent chats (they don't use the regular chat system)
    if (this.isAIAgent()) {
      return;
    }

    // Only mark as read if:
    // 1. We have a selected user
    // 2. Messages are loaded
    // 3. Chat has focus
    if (this.selectedUser && this.chatsFiltered.length > 0 && this.hasFocus) {
      const unreadMessages = this.chatsFiltered.filter(
        (g) =>
          !g.read &&
          g.senderKey === this.selectedUser.key &&
          g.recipients?.includes(this.userKey)
      );

      

      if (unreadMessages.length > 0) {
        
        const messageKeys = unreadMessages.map((m) => m.key);

        this.chatService.markAsRead(messageKeys).subscribe(() => {
          
          // Update the read status locally
          this.chatsFiltered.forEach((chat) => {
            if (messageKeys.includes(chat.key)) {
              chat.read = true;
            }
          });
        });
      }
    }
  }

  public userSelect(user: UserEntity, markRead: boolean = true) {
    this.selectedUser = user;
    this.notifications = {};
    this.itemSelected(user);

    // Reset reply message when changing users
    this.selectedReplyMessage = null;

    // Reset loading state and hasMoreChats when selecting a new user
    this.isLoadingMore = false;
    this.hasMoreChats = true; // Reset to true when selecting a new user

    // Special handling for AI Agent - load existing session if available
    if (this.isAIAgent(user)) {
      console.log('🤖 Selected AI Agent - loading chat history');
      this.loadAISessionHistory();
      return;
    }

    // Check cache first
    const cacheKey = this.selectedUser.key;
    if (this.filteredChatsCache.has(cacheKey)) {
      this.chatsFiltered = this.filteredChatsCache.get(cacheKey) || [];
    } else {
      // Filter chats where the logged-in user or the selected user is involved
      this.chatsFiltered = this.chats
        .filter(
          (g) =>
            (g.senderKey === this.userKey &&
              g.recipients.includes(this.selectedUser.key)) || // Logged-in user sent the message to the selected user
            (g.senderKey === this.selectedUser.key &&
              g.recipients.includes(this.userKey)) || // Selected user sent the message to the logged-in user
            (g.senderKey === this.selectedUser.key &&
              g.replyToSenderKey === this.userKey) || // Selected user replied to the logged-in user
            (g.senderKey === this.userKey &&
              g.replyToSenderKey === this.selectedUser.key) || // Logged-in user replied to the selected user
            (g.recipients.includes(this.userKey) &&
              g.recipients.includes(this.selectedUser.key)) // Both are recipients in the same chat
        )
        .sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        ); // Sort by oldest first

      // Cache the result
      this.filteredChatsCache.set(cacheKey, this.chatsFiltered);
    }

    this.setLastChat();

    // Wait for messages to load and scroll
    setTimeout(() => {
      this.scroll();
      if (markRead) {
        // Mark as read after a short delay to ensure messages are visible
        setTimeout(() => {
          this.checkAndMarkAsRead();
        }, 100);
      }
    }, 100);
  }

  private setLastChat() {
    if (this.chatsFiltered && this.chatsFiltered.length > 0) {
      // Get the most recent chat that's not from the current user
      this.lastChat = this.chatsFiltered.find(
        (chat) => chat.senderKey !== this.userKey
      );
    } else {
      this.lastChat = undefined;
    }
  }

  public onNoClick(): void {
    this.dialogRef.close();
  }

  public ngOnInit(): void {
    this.hasFocus = document.hasFocus();

    this.uploadFolder = `chatfiles/${this.board?.name}/${this.cardName}`;
    if (this.hasFocus) {
      this.scroll();
    }

    // Setup debounced scroll handler
    this.scrollSubscription = this.scrollSubject
      .pipe(debounceTime(150)) // Debounce scroll events by 150ms
      .subscribe((event) => {
        this.handleScrollEvent(event);
      });

    // Auto-select AI agent on init if we have users
    if (this.users && this.users.length > 0) {
      // Add AI agent to mention list
      this.mentionList = [this.aiAgentUser, ...this.users];
      this.updateMentionConfig();

      // Auto-select AI agent by default
      if (!this.selectedUser) {
        setTimeout(() => {
          this.userSelect(this.aiAgentUser, false);
        }, 500);
      }
    }

    if (this.embedded) {
      this.cardService.getAllCodesFromAllBoards().subscribe((codes) => {
        this.codes = codes;
        this.updateMentionConfig();

        // Load initial messages with pagination
        this.chatService
          .getEmbeddedChats(this.boardKey || '', this.cardKey, this.PAGE_SIZE)
          .subscribe((chats: ChatEntity[]) => {
            this.chats = chats;
            if (chats.length > 0) {
              this.lastLoadedChat = chats[chats.length - 1];
              // Subscribe to real-time updates for new messages only
              const latestCreatedAt = chats[0].createdAt;
              this.subscribeToNewEmbeddedMessages(latestCreatedAt);
            }
            this.updateChatsAndNotifications();

            // Scroll to bottom after loading
            setTimeout(() => {
              this.scroll();
            }, 100);
          });
      });
    } else {
      this.loadUsersAndChats();
    }

    if (this.board) {
      this.clientService
        .getItem(this.board?.client)
        .valueChanges()
        .subscribe((client) => {
          if (client) this.client = client.name;
        });
    }
  }

  public scroll(): void {
    setTimeout(() => {
      const objDiv: HTMLElement | null = document.getElementById('bubbles');
      if (objDiv) {
        objDiv.scrollTop = objDiv.scrollHeight;
        // After scrolling to bottom, check if we should mark as read
        this.checkAndMarkAsRead();
      }
    }, 100); // Small delay to ensure DOM has updated
  }

  public getUserTooltip(user: UserEntity): string {
    const roles = user.roles?.join(', ') || 'No roles';
    return `Email: ${user.email}\nRoles: ${roles}`;
  }

  public itemSelected(item: any): void {
    // Handle mentions from HTML editor if item is a UserEntity
    if (item && item.key && item.name) {
      // This is a UserEntity
      this.onUserMentioned(item);
      return;
    }
    
    // Original functionality for mentions
    let increment = 1;
    if (this.notifications[item?.key ? item.key : '']) {
      increment = this.notifications[item?.key ? item.key : ''] + 1;
    }

    this.notifications[item?.key ? item.key : ''] = increment;
  }

  // New method to handle user mentions from the HTML editor
  public onUserMentioned(user: UserEntity): void {
    if (user && user.key) {
      // Add the mentioned user to notifications if needed
      let increment = 1;
      if (this.notifications[user.key]) {
        increment = this.notifications[user.key] + 1;
      }
      this.notifications[user.key] = increment;
    }
  }

  public delete(index: number) {
    const chat = this.chatsFiltered[index];
    this.chatService.delete(chat.boardKey || '', chat.cardKey || '', chat.key);
  }

  public reply(index: number) {
    this.selectedReplyMessage = this.chatsFiltered[index];
    this.boardKey = this.selectedReplyMessage?.boardKey || this.boardKey;
    this.cardKey = this.selectedReplyMessage?.card || this.cardKey;
  }

  private sendNotifications(chat: ChatEntity) {
    // Get AI agent user key from board configuration
    const aiConfig = this.board?.automationConfig?.aiAgentConfig || this.board?.aiAgentConfig;
    const aiUserKey = aiConfig?.aiUserKey;
    
    const mentionedUsers = chat.recipients
      .map((mentionedUserKey) => {
        return this.users?.find((user) => user.key === mentionedUserKey);
      })
      .filter(
        (user) =>
          user !== undefined && 
          user.pushtoken && 
          user.key !== this.userKey &&
          user.key !== aiUserKey // Don't send notifications to AI agent
      ) as UserEntity[];

    console.log('📧 Notification filtering:', {
      recipients: chat.recipients,
      aiUserKey,
      filteredUsers: mentionedUsers.map(u => ({ key: u.key, name: u.name }))
    });

    return mentionedUsers.map((user) => {
      return this.notifcationService.sendNotification(user, chat as ChatEntity);
    });
  }

  private saveNotifcations() {
    // Get AI agent user key from board configuration
    const aiConfig = this.board?.automationConfig?.aiAgentConfig || this.board?.aiAgentConfig;
    const aiUserKey = aiConfig?.aiUserKey;
    
    for (const key of Object.keys(this.notifications)) {
      // Skip creating notifications for AI agent
      if (key === aiUserKey) {
        console.log('🤖 Skipping notification for AI agent:', key);
        continue;
      }
      
      const notify: NotificationEntity = new NotificationEntity();
      notify.createdAt = new Date();
      notify.updatedAt = new Date();
      notify.client = this.client;
      notify.sender = this.currentUser;
      notify.card = this.cardName ? this.cardName : '';
      notify.board = this.board?.name ? this.board?.name : '';
      notify.cardKey = this.cardKey;
      notify.boardKey = this.boardKey;
      notify.type = 'chat';
      notify.key = key;
      notify.chats = this.notifications[key];
      notify.recipientName = this.users?.find((x) => x.key == key)?.name ?? '';
      notify.recipientKey = key;

      this.notifcationService
        .create(key, notify, this.message ? this.message : '')
        .then((g: any) => {
          this.notifications = {};
        });
    }
  }

  public close() {
    this.dialogRef.close();
  }

  trackByIndex(index: number, item: any) {
    return index;
  }

  private updateChatsAndNotifications() {
    // Sort chats by creation time, oldest first
    this.chats.sort(
      (a: ChatEntity, b: ChatEntity) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    // Clear filtered chats cache since underlying data changed
    this.filteredChatsCache.clear();

    // Don't update chatsFiltered if chatting with AI agent (local messages only)
    if (this.isAIAgent()) {
      console.log('🤖 Skipping chat filter update for AI agent');
      return;
    }

    // Filter chats based on mode
    if (this.embedded) {
      const beforeFilter = this.chats.length;
      this.chatsFiltered = this.chats.filter(chat =>
        (this.boardKey && chat.boardKey === this.boardKey) &&
        (this.cardKey && chat.cardKey === this.cardKey)
      );
    } else if (this.selectedUser) {
      const beforeFilter = this.chats.length;
      this.chatsFiltered = this.chats.filter(
        (g) =>
          (g.senderKey === this.userKey &&
            g.recipients.includes(this.selectedUser.key)) || // Logged-in user sent the message to the selected user
          (g.senderKey === this.selectedUser.key &&
            g.recipients.includes(this.userKey)) || // Selected user sent the message to the logged-in user
          (g.senderKey === this.selectedUser.key &&
            g.replyToSenderKey === this.userKey) || // Selected user replied to the logged-in user
          (g.senderKey === this.userKey &&
            g.replyToSenderKey === this.selectedUser.key) || // Logged-in user replied to the selected user
          (g.recipients.includes(this.userKey) &&
            g.recipients.includes(this.selectedUser.key)) // Both are recipients in the same chat
      );
    }

    // Update user message times and unread counts
    const userLastMessageTime = new Map<string, Date>();
    const userUnreadCounts = new Map<string, number>();

    this.chats.forEach((chat) => {
      const otherUserKey =
        chat.senderKey === this.userKey ? chat.recipients[0] : chat.senderKey;
      const messageTime = new Date(chat.createdAt);

      // Update the time if it's more recent
      const currentLastTime = userLastMessageTime.get(otherUserKey);
      if (!currentLastTime || messageTime > currentLastTime) {
        userLastMessageTime.set(otherUserKey, messageTime);
      }

      // Update unread count
      if (
        !chat.read &&
        chat.senderKey === otherUserKey &&
        chat.recipients.includes(this.userKey)
      ) {
        userUnreadCounts.set(
          otherUserKey,
          (userUnreadCounts.get(otherUserKey) || 0) + 1
        );
      }
    });

    // Only update mention list if not in embedded mode
    if (!this.embedded) {
      // Sort users by unread messages first, then by last message time, then alphabetically
      const sortedUsers = this.users.sort((a, b) => {
        const aUnread = userUnreadCounts.get(a.key) || 0;
        const bUnread = userUnreadCounts.get(b.key) || 0;

        // First sort by unread count
        if (aUnread !== bUnread) {
          return bUnread - aUnread;
        }

        const aTime = userLastMessageTime.get(a.key);
        const bTime = userLastMessageTime.get(b.key);

        // Then by message time
        if (aTime && bTime) {
          return bTime.getTime() - aTime.getTime();
        }

        // If only one has messages, that one goes first
        if (aTime) return -1;
        if (bTime) return 1;

        // Finally sort alphabetically
        return a.name.localeCompare(b.name);
      });

      // Add AI Agent at the top
      this.mentionList = [this.aiAgentUser, ...sortedUsers];
    }

    // If chat is focused and we have a selected user, check for unread messages
    if (this.hasFocus && this.selectedUser) {
      this.checkAndMarkAsRead();
    }

    // Batch load card lane info for displayed messages
    this.batchLoadCardLaneInfo();

    // Scroll to the bottom when receiving new messages
    setTimeout(() => {
      this.scroll();
    }, 100);
  }

  getUnreadCount(user: UserEntity): number {
    return this.chats.filter(
      (chat) =>
        !chat.read &&
        chat.senderKey === user.key &&
        chat.recipients.includes(this.userKey)
    ).length;
  }

  private async checkAndTriggerAIAgentFromComment(mentionedUserKeys: string[], cardKey: string, boardKey: string): Promise<void> {
    console.log('🔍 Checking AI agent mention trigger:', { mentionedUserKeys, cardKey, boardKey });
    
    // Support both new automationConfig and legacy aiAgentConfig structures
    const aiConfig = this.board?.automationConfig?.aiAgentConfig || this.board?.aiAgentConfig;
    
    console.log('🤖 AI Config found:', { 
      hasAutomationConfig: !!this.board?.automationConfig?.aiAgentConfig,
      hasLegacyConfig: !!this.board?.aiAgentConfig,
      enabled: aiConfig?.enabled,
      aiUserKey: aiConfig?.aiUserKey
    });
    
    // Check if board has AI agent configuration
    if (!aiConfig || !aiConfig.enabled) {
      console.log('❌ AI agent not configured or disabled');
      return;
    }
    
    // Check if AI agent user was mentioned
    const aiUserMentioned = mentionedUserKeys.includes(aiConfig.aiUserKey);
    
    if (aiUserMentioned && cardKey) {
      // Get the card data
      try {
        const cardDoc = await this.cardService.getItem(boardKey, cardKey).get().toPromise();
        if (cardDoc && cardDoc.exists) {
          const cardData = cardDoc.data();
          if (cardData) {
            // Trigger AI agent
            await this.sharedStateService.triggerAIAgentFromComment(cardData, aiConfig);
          }
        }
      } catch (error) {
        console.error('Error triggering AI agent from chat mention:', error);
      }
    }
  }

  public async send() {
    console.log('📤 Send called, selected user:', this.selectedUser?.name, 'isAIAgent:', this.isAIAgent());

    // Check if chatting with AI agent
    if (this.isAIAgent()) {
      console.log('🤖 Routing to AI agent, message:', this.message);
      if (this.message) {
        await this.sendToAIAgent(this.message);
      } else {
        console.warn('⚠️ No message to send to AI agent');
      }
      return;
    }

    // Get mentioned users from the HTML content
    const mentionedUserNodes = this.extractMentionedUsersFromHTML(this.message || '');
    const mentionedUserKeys = mentionedUserNodes.map(user => user.key);

    // Add current recipient to mentioned users
    if (this.selectedUser && mentionedUserKeys.indexOf(this.selectedUser.key) === -1) {
      mentionedUserKeys.push(this.selectedUser.key);
    }

    // Add last chat sender if applicable
    if (this.lastChat && mentionedUserKeys.indexOf(this.lastChat.senderKey) === -1) {
      mentionedUserKeys.push(this.lastChat.senderKey);
    }

    // Always include the current user
    mentionedUserKeys.push(this.userKey);
    
    // Get card mentions from the HTML content
    const mentionedCards = await this.extractMentionedCardsFromHTML(this.message || '');

    const boardKey = this.lastChat?.boardKey || this.boardKey || '';
    const cardKey = this.lastChat?.card || this.cardKey || '';
    const replyingto = this.selectedUser?.key || '';
    
    // Ensure we have a board name if it's available
    let boardName = this.board?.name || this.lastChat?.boardName || '';
    if (!boardName && boardKey) {
      try {
        // Try to fetch the board name if we don't have it
        await this.boardService.getItem(boardKey).get().toPromise()
          .then(doc => {
            if (doc && doc.exists) {
              const boardData = doc.data();
              if (boardData && boardData.name) {
                boardName = boardData.name;
              }
            }
          });
      } catch (error) {
        console.error('Error fetching board name:', error);
      }
    }

    var chat: ChatEntity = {
      message: this.message || '',
      senderName: this.currentUser,
      senderKey: this.userKey,
      createdAt: new Date(),
      file: this.selectedImage,
      replyTo: this.selectedReplyMessage,
      recipients: mentionedUserKeys,
      replyToSenderKey: replyingto,
      boardKey: boardKey,
      cardKey: cardKey,
      card: cardKey,
      key: '',
      updatedAt: new Date(),
      read: false,
      cardName: this.cardName || this.lastChat?.cardName || '',
      boardName: boardName,
      cardsMentioned: mentionedCards,
    };

    if (chat) {
      
      this.saveNotifcations();
      this.chatService.save(boardKey || '', cardKey || '', chat).subscribe({
        next: async (key) => {
          
          // Check if AI agent was mentioned and trigger if needed
          await this.checkAndTriggerAIAgentFromComment(mentionedUserKeys, cardKey, boardKey);
          
          this.message = undefined;
          this.selectedImage = '';
          this.selectedReplyMessage = null;
          // Force an update of the chats
          if (this.embedded) {
            this.chatService.getItems(boardKey, cardKey)
              .valueChanges({ idField: 'key' })
              .subscribe((chats: ChatEntity[]) => {
                
                this.chats = chats;
                this.updateChatsAndNotifications();
                // Ensure we scroll to the bottom after sending a message
                setTimeout(() => {
                  this.scroll();
                }, 100);
              });
          } else {
            // Ensure we scroll to the bottom after sending a message
            setTimeout(() => {
              this.scroll();
            }, 100);
          }
        },
        error: (error) => {
          console.error('Error saving chat:', error);
        }
      });
    }
  }

  // New method to extract user mentions from HTML content
  private extractMentionedUsersFromHTML(htmlContent: string): UserEntity[] {
    const mentionedUsers: UserEntity[] = [];
    
    // Parse the HTML content
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    // Look for mention elements
    const mentionElements = doc.querySelectorAll('.ql-mention-denotation-char');
    mentionElements.forEach(element => {
      const userKey = element.getAttribute('data-user-key') || element.getAttribute('data-id');
      if (userKey) {
        const user = this.users.find(u => u.key === userKey);
        if (user && !mentionedUsers.some(u => u.key === userKey)) {
          mentionedUsers.push(user);
        }
      }
    });
    
    return mentionedUsers;
  }
  
  // New method to extract card mentions from HTML content
  private async extractMentionedCardsFromHTML(htmlContent: string): Promise<{ code: string; link: string; title: string }[]> {
    const mentionedCards: { code: string; link: string; title: string }[] = [];
    
    // Parse the HTML content
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    // Get all mention elements with # prefix (card mentions)
    const cardMentionElements = Array.from(doc.querySelectorAll('.ql-mention-denotation-char'))
      .filter(element => {
        const text = element.textContent || '';
        return text.startsWith('#');
      });
    
    // Process each card mention
    for (const element of cardMentionElements) {
      const text = element.textContent || '';
      if (text.startsWith('#')) {
        const cardCode = text.substring(1).trim();
        const boardId = element.getAttribute('data-id');
        
        if (boardId && cardCode) {
          const codeObj = this.codes.find(c => c.code === cardCode && c.board === boardId);
          if (codeObj) {
            try {
              const card = await this.cardService.getCardByCode(
                codeObj.board,
                codeObj.code
              );
              if (card && card.key && card.name) {
                mentionedCards.push({
                  code: codeObj.code,
                  link: `board/${codeObj.board}/${card.key}`,
                  title: card.name,
                });
              }
            } catch (error) {
              console.error('Error getting card:', error);
            }
          }
        }
      }
    }
    
    return mentionedCards;
  }

  updateMentionConfig() {
    this.mentionConfig = {
      mentions: [
        {
          items: this.mentionList as never[],
          triggerChar: '@',
          labelKey: 'name',
          valueKey: 'name',
        },
        {
          items: this.codes as never[],
          triggerChar: '#',
          labelKey: 'code',
          valueKey: 'code',
        },
      ],
    };
  }

  loadUsersAndChats() {
    this.isLoadingUsers = true;
    this.hasMoreChats = true; // Reset when loading chats

    this.cardService.getAllCodesFromAllBoards().subscribe((codes) => {
      this.codes = codes;


      // Load users
      this.userService.get().subscribe((users) => {

        this.users = users
          .filter((x: any) => x)
          .filter((user: UserEntity) => user.key !== this.userKey);

        // Add AI Agent at the top of the list if not in embedded mode
        if (!this.embedded) {
          this.mentionList = [this.aiAgentUser, ...this.users].sort((a, b) => {
            // AI Agent always at top
            if (a.key === this.AI_AGENT_USER_KEY) return -1;
            if (b.key === this.AI_AGENT_USER_KEY) return 1;
            return a.name.localeCompare(b.name);
          });
        } else {
          this.mentionList = [...this.users].sort((a, b) =>
            a.name.localeCompare(b.name)
          );
        }
        

        // Load initial historical chats with limit
        this.chatService.getHistoricalChats(this.userKey, this.PAGE_SIZE)
          .subscribe((chats) => {
            
            this.chats = Utils.convertTimestampsInObject(chats);
            
            if (chats.length > 0) {
              this.lastLoadedChat = chats[chats.length - 1];
              // Start real-time subscription for new messages
              const latestCreatedAt = chats[0].createdAt;
              this.subscribeToNewMessages(latestCreatedAt);
            }

            // Create a map of user's last message time
            const userLastMessageTime = new Map<string, Date>();
            
            this.chats.forEach(chat => {
              const otherUserKey = chat.senderKey === this.userKey ? chat.recipients[0] : chat.senderKey;
              const messageTime = chat.createdAt instanceof Date 
                ? chat.createdAt 
                : (chat.createdAt as any).toDate();
              
              const currentLastTime = userLastMessageTime.get(otherUserKey);
              if (!currentLastTime || messageTime > currentLastTime) {
                userLastMessageTime.set(otherUserKey, messageTime);
              }
            });

            // Final sort by last message time, then alphabetically
            this.mentionList = this.users.sort((a, b) => {
              const aTime = userLastMessageTime.get(a.key);
              const bTime = userLastMessageTime.get(b.key);

              if (aTime && bTime) {
                return bTime.getTime() - aTime.getTime();
              }
              
              if (aTime) return -1;
              if (bTime) return 1;
              
              return a.name.localeCompare(b.name);
            });

            this.updateMentionConfig();
            
            if (this.mentionList.length > 0 && !this.selectedUser) {
              this.userSelect(this.mentionList[0], false);
              
              if (this.hasFocus) {
                setTimeout(() => {
                  this.checkAndMarkAsRead();
                }, 1000);
              }
            }

            this.updateChatsAndNotifications();
            setTimeout(() => {
              this.isLoadingUsers = false;

              // Auto-select AI agent by default if no user is selected
              if (!this.selectedUser && !this.embedded) {
                setTimeout(() => {
                  this.userSelect(this.aiAgentUser, false);
                }, 200);
              }
            }, 100);
          });
      });
    });
  }

  private subscribeToNewMessages(afterTimestamp: Date) {
    if (this.realtimeSubscription) {
      this.realtimeSubscription.unsubscribe();
    }

    this.realtimeSubscription = this.chatService
      .getRealtimeChats(this.userKey, afterTimestamp)
      .subscribe(newChats => {
        if (newChats.length > 0) {
          // Reset reply message when new messages arrive
          this.selectedReplyMessage = null;

          // Merge new messages with existing ones
          const allChats = [...newChats, ...this.chats];
          // Remove duplicates by key and sort
          this.chats = Array.from(
            new Map(allChats.map(chat => [chat.key, chat])).values()
          ).sort((a, b) => {
            const aDate = a.createdAt instanceof Date ?
              a.createdAt :
              (a.createdAt as any).toDate();
            const bDate = b.createdAt instanceof Date ?
              b.createdAt :
              (b.createdAt as any).toDate();
            return bDate.getTime() - aDate.getTime();
          });
          this.updateChatsAndNotifications();
        }
      });
  }

  private subscribeToNewEmbeddedMessages(afterTimestamp: Date) {
    if (this.realtimeSubscription) {
      this.realtimeSubscription.unsubscribe();
    }

    this.realtimeSubscription = this.chatService
      .getRealtimeEmbeddedChats(this.boardKey || '', this.cardKey, afterTimestamp)
      .subscribe(newChats => {
        if (newChats.length > 0) {
          // Reset reply message when new messages arrive
          this.selectedReplyMessage = null;

          // Merge new messages with existing ones
          const allChats = [...newChats, ...this.chats];
          // Remove duplicates by key and sort
          this.chats = Array.from(
            new Map(allChats.map(chat => [chat.key, chat])).values()
          ).sort((a, b) => {
            const aDate = a.createdAt instanceof Date ?
              a.createdAt :
              (a.createdAt as any).toDate();
            const bDate = b.createdAt instanceof Date ?
              b.createdAt :
              (b.createdAt as any).toDate();
            return aDate.getTime() - bDate.getTime(); // Oldest first for embedded
          });
          this.updateChatsAndNotifications();

          // Scroll to bottom when new messages arrive
          setTimeout(() => {
            this.scroll();
          }, 100);
        }
      });
  }

  loadMoreChats(): void {
    if (this.isLoadingMore || !this.lastLoadedChat) return;
    
    this.isLoadingMore = true;
    this.chatService.getHistoricalChats(
      this.userKey, 
      this.PAGE_SIZE, 
      this.lastLoadedChat.createdAt,
      this.selectedUser?.key
    ).subscribe((olderChats) => {
      if (olderChats.length > 0) {
        // Convert timestamps and prepend to the beginning of the chats array
        const convertedChats = Utils.convertTimestampsInObject(olderChats);
        this.chats = [...this.chats, ...convertedChats]; // Append to end because they're older
        this.lastLoadedChat = olderChats[olderChats.length - 1];
        
        // Don't need to re-sort since the service should return chats in timestamp order
        if (this.selectedUser) {
          // Filter for the current user without re-sorting
          const filteredOlderChats = convertedChats.filter((g: ChatEntity) =>
            (g.senderKey === this.userKey && g.recipients.includes(this.selectedUser.key)) ||
            (g.senderKey === this.selectedUser.key && g.recipients.includes(this.userKey)) ||
            (g.senderKey === this.selectedUser.key && g.replyToSenderKey === this.userKey) ||
            (g.senderKey === this.userKey && g.replyToSenderKey === this.selectedUser.key) ||
            (g.recipients.includes(this.userKey) && g.recipients.includes(this.selectedUser.key))
          );
          
          // Add older messages to the end of filtered chats array
          this.chatsFiltered = [...this.chatsFiltered, ...filteredOlderChats];
        }
      } else {
        // No more messages to load
        this.hasMoreChats = false;
      }
      this.isLoadingMore = false;
    });
  }

  /**
   * Initialize the Flaredeck AI Agent user
   */
  private initializeAIAgentUser(): void {
    this.aiAgentUser = {
      key: this.AI_AGENT_USER_KEY,
      email: 'agent@flaredeck.app',
      name: 'Flaredeck Agent',
      roles: ['AI Assistant'],
      role: 'AI Assistant',
      permission: 'agent',
      isActive: true,
      isAdmin: false,
      createdAt: new Date(),
      updatedAt: new Date(),
      photoURL: 'assets/img/flaredeck_logo_closed.svg',
      pushtoken: null,
      cardCount: 0,
      averageScore: 0,
      totalScore: 0,
      rankNumber: 0,
      roleBasedScores: {},
      messageCount: 0,
      latestMessageTimestamp: new Date(),
      profile: 'assets/img/flaredeck_logo_closed.svg' // Avatar component uses profile as image URL
    };
  }

  /**
   * Check if the selected user is the AI agent
   */
  public isAIAgent(user?: UserEntity): boolean {
    if (!user) {
      user = this.selectedUser;
    }
    return user?.key === this.AI_AGENT_USER_KEY;
  }

  /**
   * Convert markdown to safe HTML
   */
  public convertMarkdownToHtml(markdown: string): string {
    try {
      const html = marked.parse(markdown, { async: false }) as string;
      return html;
    } catch (error) {
      console.error('Error converting markdown:', error);
      return markdown; // Return original if conversion fails
    }
  }

  /**
   * Send message to AI agent
   */
  private async sendToAIAgent(message: string): Promise<void> {
    try {
      console.log('🤖 Sending message to AI agent:', message);
      console.log('🤖 Current session ID:', this.currentAISessionId);

      // Show user's message immediately
      const userChat: ChatEntity = {
        message: message,
        senderName: this.currentUser,
        senderKey: this.userKey,
        createdAt: new Date(),
        file: this.selectedImage,
        replyTo: this.selectedReplyMessage,
        recipients: [this.AI_AGENT_USER_KEY],
        replyToSenderKey: this.AI_AGENT_USER_KEY,
        boardKey: this.boardKey || '',
        cardKey: this.cardKey || '',
        card: this.cardKey || '',
        key: 'temp-' + Date.now(),
        updatedAt: new Date(),
        read: true,
        cardName: this.cardName || '',
        boardName: this.board?.name || '',
        cardsMentioned: [],
      };

      this.chatsFiltered.push(userChat);
      this.message = undefined;
      this.selectedImage = '';
      this.selectedReplyMessage = null;

      setTimeout(() => {
        this.scroll();
      }, 100);

      // Show typing indicator
      this.isAITyping = true;

      // Send to AI agent
      console.log('🤖 Calling aiAgentChatService.sendMessage...');
      this.aiAgentChatService.sendMessage(message, this.currentAISessionId).subscribe({
        next: (response) => {
          console.log('✅ AI agent response received:', response);

          // Hide typing indicator
          this.isAITyping = false;

          // Store session ID for continuity
          this.currentAISessionId = response.sessionId;

          // Convert markdown to HTML
          const htmlMessage = this.convertMarkdownToHtml(response.response);

          // Show AI agent's response
          const aiChat: ChatEntity = {
            message: htmlMessage,
            senderName: 'Flaredeck Agent',
            senderKey: this.AI_AGENT_USER_KEY,
            createdAt: new Date(),
            file: '',
            replyTo: null,
            recipients: [this.userKey],
            replyToSenderKey: this.userKey,
            boardKey: this.boardKey || '',
            cardKey: this.cardKey || '',
            card: this.cardKey || '',
            key: 'ai-' + Date.now(),
            updatedAt: new Date(),
            read: true,
            cardName: this.cardName || '',
            boardName: this.board?.name || '',
            cardsMentioned: [],
          };

          this.chatsFiltered.push(aiChat);

          setTimeout(() => {
            this.scroll();
          }, 100);
        },
        error: (error) => {
          console.error('❌ Error sending to AI agent:', error);
          console.error('❌ Error details:', JSON.stringify(error, null, 2));

          // Hide typing indicator
          this.isAITyping = false;

          // Show error message with more details
          let errorMessage = 'Sorry, I encountered an error. ';
          if (error?.message) {
            errorMessage += error.message;
          } else if (error?.error?.message) {
            errorMessage += error.error.message;
          } else {
            errorMessage += 'Please try again.';
          }

          const errorChat: ChatEntity = {
            message: errorMessage,
            senderName: 'Flaredeck Agent',
            senderKey: this.AI_AGENT_USER_KEY,
            createdAt: new Date(),
            file: '',
            replyTo: null,
            recipients: [this.userKey],
            replyToSenderKey: this.userKey,
            boardKey: this.boardKey || '',
            cardKey: this.cardKey || '',
            card: this.cardKey || '',
            key: 'ai-error-' + Date.now(),
            updatedAt: new Date(),
            read: true,
            cardName: this.cardName || '',
            boardName: this.board?.name || '',
            cardsMentioned: [],
          };

          this.chatsFiltered.push(errorChat);

          setTimeout(() => {
            this.scroll();
          }, 100);
        }
      });
    } catch (error) {
      console.error('❌ Exception in sendToAIAgent:', error);
    }
  }

  /**
   * Show AI welcome message with available tools based on user permission
   * Only shows if chat is currently empty to prevent duplicates
   */
  private showAIWelcomeMessage(): void {
    // Prevent duplicate welcome messages
    if (this.chatsFiltered.length > 0) {
      console.log('⚠️ Skipping welcome message - chat not empty');
      return;
    }
    const userPermission = this.currentUserEntity?.permission || 'User';
    const isSuperAdmin = userPermission === 'Super-Admin';

    // Define tool categories
    const regularTools = [
      { name: 'Board Management', examples: ['List my boards', 'Show me cards on the board', 'Update card status'] },
      { name: 'Timesheet Management', examples: ['Show timesheet options', 'Log 2 hours to project X', 'Show my timesheet entries'] },
      { name: 'Card Operations', examples: ['Create a new card', 'Add a comment to card', 'Get card details'] }
    ];

    const superAdminTools = [
      { name: 'Client Management', examples: ['Create a new client called Acme Corp', 'Add client information'] },
      { name: 'Project Management', examples: ['Create a billable item for Client X', 'Add a new project with rate $150/hour'] },
      { name: 'User Management', examples: ['List all users', 'Show me John\'s timesheet', 'Email Sarah a reminder'] }
    ];

    // Build welcome message
    let welcomeMessage = `👋 **Welcome! I'm your Flaredeck AI Assistant**\n\n`;
    welcomeMessage += `I'm here to help you manage your projects, boards, cards, and timesheets. `;
    welcomeMessage += `You can ask me questions in natural language!\n\n`;

    // Add regular tools
    welcomeMessage += `**📋 What I can help you with:**\n\n`;
    regularTools.forEach(category => {
      welcomeMessage += `**${category.name}**\n`;
      category.examples.forEach(example => {
        welcomeMessage += `  • "${example}"\n`;
      });
      welcomeMessage += `\n`;
    });

    // Add Super-Admin tools if applicable
    if (isSuperAdmin) {
      welcomeMessage += `\n**🔐 Super-Admin Tools** (available to you):\n\n`;
      superAdminTools.forEach(category => {
        welcomeMessage += `**${category.name}**\n`;
        category.examples.forEach(example => {
          welcomeMessage += `  • "${example}"\n`;
        });
        welcomeMessage += `\n`;
      });
    }

    welcomeMessage += `\n💡 **Tip:** Just ask me anything in plain English and I'll help you out!`;

    // Convert markdown to HTML
    const htmlMessage = this.convertMarkdownToHtml(welcomeMessage);

    // Create welcome chat message
    const welcomeChat = {
      key: `welcome-${Date.now()}`,
      message: htmlMessage,
      senderName: this.aiAgentUser.name,
      senderKey: this.AI_AGENT_USER_KEY,
      photoURL: this.aiAgentUser.photoURL,
      read: false,
      timestamp: new Date(),
      type: 'text',
      isAIMessage: true
    };

    // Add to chat
    this.chatsFiltered.push(welcomeChat);
    setTimeout(() => this.scroll(), 100);
  }

  /**
   * Load AI agent chat history from most recent session
   */
  private async loadAISessionHistory(): Promise<void> {
    try {
      this.isLoadingHistory = true;
      this.chatsFiltered = [];

      // Get list of sessions for current user
      this.aiAgentChatService.listSessions(1).subscribe({
        next: async (sessions) => {
          if (sessions && sessions.length > 0) {
            // Load the most recent session
            const latestSession = sessions[0];
            console.log('📂 Loading AI session:', latestSession.id);

            this.currentAISessionId = latestSession.id;

            // Get session with messages
            this.aiAgentChatService.getSession(latestSession.id).subscribe({
              next: (sessionData) => {
                console.log('💬 Loaded messages:', sessionData.messages.length);

                // Convert session messages to chat entities
                this.chatsFiltered = sessionData.messages.map((msg, index) => {
                  const isUser = msg.role === 'user';
                  const htmlMessage = isUser ? msg.content : this.convertMarkdownToHtml(msg.content);

                  return {
                    key: `history-${index}`,
                    message: htmlMessage,
                    senderName: isUser ? this.currentUser : 'Flaredeck Agent',
                    senderKey: isUser ? this.userKey : this.AI_AGENT_USER_KEY,
                    createdAt: msg.timestamp,
                    file: '',
                    replyTo: null,
                    recipients: [isUser ? this.AI_AGENT_USER_KEY : this.userKey],
                    replyToSenderKey: isUser ? this.AI_AGENT_USER_KEY : this.userKey,
                    boardKey: this.boardKey || '',
                    cardKey: this.cardKey || '',
                    card: this.cardKey || '',
                    updatedAt: msg.timestamp,
                    read: true,
                    cardName: this.cardName || '',
                    boardName: this.board?.name || '',
                    cardsMentioned: [],
                  } as ChatEntity;
                });

                this.isLoadingHistory = false;
                setTimeout(() => {
                  this.scroll();
                }, 100);
              },
              error: (error) => {
                console.error('❌ Error loading session:', error);
                this.isLoadingHistory = false;
                // Show welcome message on error
                this.showAIWelcomeMessage();
              }
            });
          } else {
            // No existing sessions, show welcome message
            console.log('📭 No existing sessions found');
            this.currentAISessionId = undefined;
            this.isLoadingHistory = false;
            this.showAIWelcomeMessage();
          }
        },
        error: (error) => {
          console.error('❌ Error listing sessions:', error);
          this.isLoadingHistory = false;
          // Show welcome message on error
          this.showAIWelcomeMessage();
        }
      });
    } catch (error) {
      console.error('❌ Exception in loadAISessionHistory:', error);
      this.isLoadingHistory = false;
      this.showAIWelcomeMessage();
    }
  }

  /**
   * Clear AI agent chat history and start new conversation
   */
  public clearAIHistory(): void {
    if (!this.isAIAgent()) {
      return;
    }

    if (confirm('Are you sure you want to start a new conversation? This will clear your current chat history.')) {
      console.log('🗑️ Clearing AI chat history');

      // Delete the current session if it exists
      if (this.currentAISessionId) {
        this.aiAgentChatService.deleteSession(this.currentAISessionId).subscribe({
          next: () => {
            console.log('✅ Session deleted successfully');
          },
          error: (error) => {
            console.error('❌ Error deleting session:', error);
          }
        });
      }

      // Reset local state
      this.currentAISessionId = undefined;
      this.chatsFiltered = [];
      this.isLoadingHistory = false;

      // Show welcome message ONCE
      this.showAIWelcomeMessage();

      setTimeout(() => {
        this.scroll();
      }, 100);
    }
  }

  /**
   * Batch load card lane info for all chats to avoid individual API calls
   */
  private batchLoadCardLaneInfo(): void {
    if (!this.chatsFiltered || this.chatsFiltered.length === 0) {
      return;
    }

    // Get unique card keys that need lane info
    const uniqueCards = new Set<string>();
    this.chatsFiltered.forEach(chat => {
      const boardKey = chat.boardKey;
      const cardKey = chat.cardKey || chat.card;
      if (boardKey && cardKey) {
        const cacheKey = `${boardKey}:${cardKey}`;
        if (!this.cardLaneCache.has(cacheKey)) {
          uniqueCards.add(cacheKey);
        }
      }
    });

    // Batch load lane info for cards not in cache
    uniqueCards.forEach(cacheKey => {
      const [boardKey, cardKey] = cacheKey.split(':');
      this.cardService.getCardLaneInfo(boardKey, cardKey).subscribe(cardInfo => {
        if (cardInfo && cardInfo.laneText) {
          this.cardLaneCache.set(cacheKey, cardInfo.laneText);
        }
      });
    });
  }

  /**
   * Get card lane info from cache
   */
  public getCardLane(chat: ChatEntity): string | null {
    const boardKey = chat.boardKey;
    const cardKey = chat.cardKey || chat.card;
    if (boardKey && cardKey) {
      const cacheKey = `${boardKey}:${cardKey}`;
      return this.cardLaneCache.get(cacheKey) || null;
    }
    return null;
  }

  ngOnDestroy() {
    if (this.realtimeSubscription) {
      this.realtimeSubscription.unsubscribe();
    }
    if (this.chatListen) {
      this.chatListen.unsubscribe();
    }
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }
  }

  // Updated method to load older messages
  public loadOlderMessages(): void {
    if (this.isLoadingMore || this.chatsFiltered.length === 0 || !this.hasMoreChats) {
      return;
    }

    this.isLoadingMore = true;

    // Reset reply message when loading new chats
    this.selectedReplyMessage = null;

    // Show loading indicator
    const loadingIndicator = document.querySelector('.loading-more') as HTMLElement;
    if (loadingIndicator) {
      loadingIndicator.style.display = 'block';
      loadingIndicator.style.opacity = '1';
    }

    const oldestChat = this.chatsFiltered[0];

    if (!oldestChat || !oldestChat.createdAt) {
      this.isLoadingMore = false;
      this.hideLoadingIndicator();
      return;
    }

    const bubbles = document.getElementById('bubbles');
    const prevScrollHeight = bubbles ? bubbles.scrollHeight : 0;
    const scrollPosition = bubbles ? bubbles.scrollTop : 0;

    // Use different service method based on embedded mode
    const loadChatsObservable = this.embedded
      ? this.chatService.getEmbeddedChatsBeforeTimestamp(
          this.boardKey || '',
          this.cardKey,
          this.PAGE_SIZE,
          oldestChat.createdAt
        )
      : this.chatService.getHistoricalChats(
          this.userKey,
          this.PAGE_SIZE,
          oldestChat.createdAt,
          this.selectedUser?.key,
          this.boardKey
        );

    loadChatsObservable.subscribe({
      next: (chats) => {
        if (chats.length === 0) {
          this.hasMoreChats = false;
          this.isLoadingMore = false;
          this.hideLoadingIndicator();
          return;
        }

        // Process and add chats
        const processedChats = Utils.convertTimestampsInObject(chats);
        this.chatsFiltered = [...processedChats, ...this.chatsFiltered];

        // Adjust scroll position
        setTimeout(() => {
          this.isLoadingMore = false;
          this.hideLoadingIndicator();

          if (bubbles) {
            const newScrollHeight = bubbles.scrollHeight;
            const heightDifference = newScrollHeight - prevScrollHeight;
            bubbles.scrollTop = scrollPosition + heightDifference;
          }
        }, 300);
      },
      error: (err) => {
        console.error('Error loading older messages:', err);
        this.isLoadingMore = false;
        this.hideLoadingIndicator();
      }
    });
  }
  
  private hideLoadingIndicator(): void {
    const loadingIndicator = document.querySelector('.loading-more') as HTMLElement;
    if (loadingIndicator) {
      loadingIndicator.style.opacity = '0';
      setTimeout(() => {
        if (!this.isLoadingMore) {
          loadingIndicator.style.display = 'none';
        }
      }, 300);
    }
  }
}
